import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge, Box, Button, Flex, Progress, SimpleGrid, Spinner, Table, Tbody, Td, Text, Th, Thead, Tr, VStack,
} from "@chakra-ui/react";
import { RefreshCw } from "lucide-react";
import { getMetricasAnunciosTrocas } from "../../services/api";
import { ErroCarga, Kpi, Vazio, dataHoraBR, duracao, num, useCelular } from "../SaudeAnunciosML/comum";
import { BadgeTipo, COR_CLASSICO, COR_PREMIUM, Cartao, TAMANHO_PEQUENO, brl } from "./base";

const INTERVALO = 30000;

const STATUS = {
  em_andamento: { label: "Em andamento", cor: "blue" },
  concluida: { label: "Concluída", cor: "green" },
  falhou: { label: "Falhou", cor: "red" },
  interrompida: { label: "Interrompida", cor: "orange" },
};

const ORIGEM = { regra_impar_par: "Regra ímpar/par", teste_ida_volta: "Teste de ida e volta", tarifa_frete_real: "Recálculo com tarifa e frete reais" };
const STATUS_ANUNCIO = { active: "ativos", paused: "pausados" };

function parametros(e) {
  try {
    return JSON.parse(e.parametros || "{}") || {};
  } catch (err) {
    return {};
  }
}

function Execucao({ e }) {
  const p = parametros(e);
  const s = STATUS[e.status] || { label: e.status, cor: "gray" };
  const pctFeito = e.total > 0 ? Math.min(100, (Number(e.processados) / Number(e.total)) * 100) : 0;
  return (
    <Box p={3} borderWidth="1px" borderColor="gray.100" borderRadius="md">
      <Flex justify="space-between" gap={2} flexWrap="wrap" align="center">
        <Flex gap={2} align="center" flexWrap="wrap">
          <Text fontWeight="semibold" fontSize="sm">#{e.id} · Conta {p.conta ?? "—"}</Text>
          <Badge colorScheme={s.cor} fontSize="0.7em">{s.label}</Badge>
          <Text fontSize="xs" color="gray.600">{ORIGEM[e.origem] || e.origem}{p.status ? ` · ${String(p.status).split(",").map((x) => STATUS_ANUNCIO[x.trim()] || x).join(" e ")}` : ""}</Text>
        </Flex>
        <Text fontSize="xs" color="gray.600">{dataHoraBR(e.created_at)} · {duracao(e.created_at, e.finalizado_em)}</Text>
      </Flex>
      <Progress value={pctFeito} size="sm" colorScheme={e.status === "em_andamento" ? "orange" : "green"} borderRadius="full" mt={2}
        aria-label={`Execução ${e.id}: ${num(pctFeito, 1)}% analisado`} />
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={2} mt={2} fontSize="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>
        <Text>Analisados: <b>{num(e.processados)}</b> de {num(e.total)} ({num(pctFeito, 1)}%)</Text>
        <Text>Alterados: <b>{num(e.ok)}</b></Text>
        <Text>Falhas: <b>{num(e.falhas)}</b></Text>
        <Text>Sem mudança: <b>{num(e.ignorados)}</b></Text>
        {e.erro && <Text color="orange.700" noOfLines={2} title={e.erro}>{e.erro}</Text>}
      </SimpleGrid>
    </Box>
  );
}

export default function TrocasTab({ onAbrir }) {
  const celular = useCelular();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(false);
  const ultima = useRef(0);

  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    const minha = ++ultima.current;
    if (!silencioso) setCarregando(true);
    const r = await getMetricasAnunciosTrocas();
    if (minha !== ultima.current) return;
    setCarregando(false);
    setErro(!r);
    if (r) setDados(r);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { ultima.current += 1; }, []);

  const emAndamento = !!dados?.em_andamento;
  useEffect(() => {
    if (!emAndamento) return undefined;
    const id = setInterval(() => { if (!document.hidden) carregar({ silencioso: true }); }, INTERVALO);
    return () => clearInterval(id);
  }, [emAndamento, carregar]);

  const porConta = useMemo(() => {
    const m = { 1: {}, 2: {}, 3: {} };
    (dados?.por_tipo || []).forEach((l) => {
      const c = Number(l.conta_id);
      if (m[c]) m[c][l.tipo] = (m[c][l.tipo] || 0) + Number(l.n);
    });
    return m;
  }, [dados]);

  return (
    <Box p={{ base: 3, md: 5 }}>
      <Flex gap={2} mb={4} align="center" flexWrap="wrap">
        <Button size={TAMANHO_PEQUENO} variant="outline" leftIcon={<RefreshCw size={14} />} onClick={() => carregar()} isLoading={carregando}>Atualizar</Button>
        {emAndamento && <Text fontSize="xs" color="gray.600">Atualiza sozinho a cada 30 s enquanto houver troca em andamento.</Text>}
      </Flex>

      {erro && !dados && <ErroCarga onTentar={() => carregar()} carregando={carregando} />}
      {!dados && !erro && <Flex justify="center" py={10}><Spinner color="orange.400" /></Flex>}

      {dados && (
        <VStack align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 2, md: 4, xl: 6 }} spacing={3}>
            {[1, 2, 3].map((c) => {
              const t = porConta[c] || {};
              return (
                <React.Fragment key={c}>
                  <Kpi label={`Conta ${c}: Premium`} valor={num(t.gold_pro || 0)} ajuda="anúncios ativos e pausados" cor={COR_PREMIUM} />
                  <Kpi label={`Conta ${c}: Clássico`} valor={num(t.gold_special || 0)}
                    ajuda={t.nao_lido ? `${num(t.nao_lido)} ainda sem tipo lido` : "anúncios ativos e pausados"} cor={COR_CLASSICO} />
                </React.Fragment>
              );
            })}
          </SimpleGrid>

          <Cartao titulo="Execuções">
            {!dados.execucoes?.length ? <Text fontSize="sm" color="gray.600">Nenhuma execução ainda.</Text> : (
              <VStack align="stretch" spacing={2}>
                {dados.execucoes.filter((e) => !Number(e.dry_run)).map((e) => <Execucao key={e.id} e={e} />)}
              </VStack>
            )}
          </Cartao>

          <Cartao titulo={`Falhas recentes (${num(dados.falhas?.length || 0)})`}>
            {!dados.falhas?.length ? <Vazio titulo="Nenhuma falha." /> : celular ? (
              <VStack align="stretch" spacing={0}>
                {dados.falhas.map((f, i) => (
                  <Box key={`${f.id_ml}-${i}`} py={2} borderBottomWidth="1px" borderColor="gray.100" as="button" type="button" textAlign="left" onClick={() => onAbrir(f.id_ml)}>
                    <Flex gap={2} align="center" flexWrap="wrap">
                      <Text fontSize="sm" fontWeight="semibold">{f.id_ml}</Text>
                      <Text fontSize="xs" color="gray.600">Conta {f.conta_id} · {dataHoraBR(f.created_at)}</Text>
                    </Flex>
                    <Text fontSize="xs" color="red.700" mt={1}>{f.erro || f.status}</Text>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Box overflowX="auto">
                <Table size="sm">
                  <Thead><Tr><Th>Anúncio</Th><Th>Conta</Th><Th>Troca</Th><Th isNumeric>Preço</Th><Th>Erro</Th><Th>Quando</Th></Tr></Thead>
                  <Tbody>
                    {dados.falhas.map((f, i) => (
                      <Tr key={`${f.id_ml}-${i}`} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => onAbrir(f.id_ml)}>
                        <Td whiteSpace="nowrap">{f.id_ml}</Td>
                        <Td>{f.conta_id}</Td>
                        <Td whiteSpace="nowrap"><BadgeTipo tipo={f.tipo_antes} /> → <BadgeTipo tipo={f.tipo_depois} /></Td>
                        <Td isNumeric>{brl(f.preco_antes)}</Td>
                        <Td maxW="420px"><Text fontSize="xs" color="red.700" noOfLines={2} title={f.erro || ""}>{f.erro || f.status}</Text></Td>
                        <Td whiteSpace="nowrap">{dataHoraBR(f.created_at)}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Cartao>
        </VStack>
      )}
    </Box>
  );
}
